import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Store, Package, Search, Tag, FileSpreadsheet, Warehouse, Trash2, ArrowRight, Plus, X, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown, Image, Check, Scan, Loader2, Pencil, XCircle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';
import api from '../lib/api';
import { getPriceWithoutTVA, getTVAAmount, normalizeBarcode } from '../lib/utils';

// ==================== SOUND HELPERS ====================
const audioCtx = typeof window !== 'undefined' ? new (window.AudioContext || window.webkitAudioContext)() : null;

const playSound = (type) => {
    if (!audioCtx) return;
    // Resume context if suspended (autoplay policy)
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const oscillator = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    oscillator.connect(gain);
    gain.connect(audioCtx.destination);

    if (type === 'ok') {
        // Short high-pitched double beep
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(1200, audioCtx.currentTime);
        oscillator.frequency.setValueAtTime(1600, audioCtx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.18, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.18);
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 0.18);
    } else {
        // Low buzzer for error
        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(280, audioCtx.currentTime);
        oscillator.frequency.setValueAtTime(200, audioCtx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.2, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.35);
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 0.35);
    }
};

// Format price helper
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

// Check if V3 (TVA mode) is active
const isV3 = () => localStorage.getItem('app_version') === 'v3';

export const MagazinV2 = () => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ total_quantity: 0, total_value: 0, unique_products: 0 });
    const [search, setSearch] = useState('');
    
    // Move to Depot modal
    const [showMoveDepot, setShowMoveDepot] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [moveQty, setMoveQty] = useState(1);
    const [depotPallets, setDepotPallets] = useState([]);
    const [selectedPallet, setSelectedPallet] = useState(null);
    const [selectedBox, setSelectedBox] = useState(null);
    const [showNewPallet, setShowNewPallet] = useState(false);
    const [showNewBox, setShowNewBox] = useState(false);
    const [newPalletNumber, setNewPalletNumber] = useState('');
    const [newBoxName, setNewBoxName] = useState('');
    
    // Move to Scrap modal
    const [showMoveScrap, setShowMoveScrap] = useState(false);
    const [scrapReason, setScrapReason] = useState('');
    const [scrapReasons, setScrapReasons] = useState(['Defect', 'Deteriorat', 'Expirat', 'Pierdere']);
    
    // Scan modal for choosing destination
    const [showScanChoice, setShowScanChoice] = useState(false);
    const [scannedProduct, setScannedProduct] = useState(null);
    const [showScanner, setShowScanner] = useState(false);
    
    // NEW: Scan from depot to store
    const [showDepotScanner, setShowDepotScanner] = useState(false);
    const [depotProduct, setDepotProduct] = useState(null);
    const [showDepotMoveModal, setShowDepotMoveModal] = useState(false);
    const [depotMoveQty, setDepotMoveQty] = useState(1);
    const [depotItemInfo, setDepotItemInfo] = useState(null);
    
    // Expanded categories
    const [expandedCategories, setExpandedCategories] = useState({});
    
    // Sorting state - default alphabetic by name
    const [sortBy, setSortBy] = useState('name'); // name, barcode, quantity, category
    const [sortDir, setSortDir] = useState('asc'); // asc, desc
    
    // Image lightbox
    const [lightboxImage, setLightboxImage] = useState(null);
    
    // ADD PRODUCT: Double scan flow (identical to Depot)
    const [showAddModal, setShowAddModal] = useState(false);
    const [addScan1, setAddScan1] = useState('');
    const [addScan2, setAddScan2] = useState('');
    const [addScanValidated, setAddScanValidated] = useState(false);
    const [addValidatedBorder, setAddValidatedBorder] = useState(false);
    const [addDescription, setAddDescription] = useState('');
    const [addQty, setAddQty] = useState(1);
    const [addPurchasePrice, setAddPurchasePrice] = useState('');
    const [addMinSalePrice, setAddMinSalePrice] = useState('');
    const [addFetchedPrice, setAddFetchedPrice] = useState(0);
    const [addFetchedMinPrice, setAddFetchedMinPrice] = useState(0);
    const [addPriceSource, setAddPriceSource] = useState('');
    const [addScanError, setAddScanError] = useState('');
    const [addShortCodeWarning, setAddShortCodeWarning] = useState(false);
    const [addExistingProduct, setAddExistingProduct] = useState(null);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [addConfirmHigh, setAddConfirmHigh] = useState(null);
    const [addRecentItems, setAddRecentItems] = useState([]);
    const addScan1Ref = useRef(null);
    const addScan2Ref = useRef(null);
    const addDescRef = useRef(null);
    const addQtyRef = useRef(null);
    const addPriceRef = useRef(null);
    const addMinPriceRef = useRef(null);
    
    // Category editing
    const [editingCategoryBarcode, setEditingCategoryBarcode] = useState(null);
    const [editCategoryValue, setEditCategoryValue] = useState('');
    
    // Price data cache (pret_mediu_min, pret_vanzare_min)
    const [priceDataCache, setPriceDataCache] = useState({});
    
    // Alphabet navigator refs
    const itemRefs = useRef({});

    useEffect(() => {
        // Ensure V2 version is set
        loadStore();
        loadScrapReasons();
        
        // Listen for scan event from FAB button
        const handleScan = () => {
            setShowScanner(true);
        };
        window.addEventListener('v2-scan-magazin', handleScan);
        
        return () => {
            window.removeEventListener('v2-scan-magazin', handleScan);
        };
    }, []);
    
    const loadScrapReasons = async () => {
        try {
            const res = await api.get('/scrap/v2/reasons');
            setScrapReasons(res.data || ['Defect', 'Deteriorat', 'Expirat', 'Pierdere']);
        } catch (err) {
            console.debug('Scrap reasons not loaded, using defaults:', err.message);
        }
    };

    // ===================== ADD PRODUCT FLOW (identical to Depot) =====================
    const resetAddForm = () => {
        setAddScan1(''); setAddScan2(''); setAddScanValidated(false); setAddValidatedBorder(false);
        setAddDescription(''); setAddQty(1); setAddPurchasePrice('');
        setAddMinSalePrice(''); setAddFetchedPrice(0); setAddFetchedMinPrice(0);
        setAddPriceSource(''); setAddScanError(''); setAddShortCodeWarning(false);
        setAddExistingProduct(null); setAddConfirmHigh(null);
    };

    const handleAddScan1Change = (e) => {
        const value = normalizeBarcode(e.target.value);
        setAddScan1(value);
        setAddScanError('');
        setAddShortCodeWarning(false);
    };

    const handleAddScan1KeyDown = (e) => {
        if (e.key === 'Enter' && addScan1.trim()) {
            e.preventDefault();
            if (addScan1.trim().length < 5) {
                setAddShortCodeWarning(true);
                toast.warning('Cod scurt - verifică eticheta!', { duration: 2000 });
            }
            addScan2Ref.current?.focus();
        }
    };

    const handleAddScan2Change = (e) => {
        const value = normalizeBarcode(e.target.value);
        setAddScan2(value);
    };

    const handleAddScan2KeyDown = async (e) => {
        if (e.key === 'Enter' && addScan2.trim()) {
            e.preventDefault();
            if (addScan1.trim() !== addScan2.trim()) {
                setAddScanError('Eroare scanare: codurile nu coincid!');
                playSound('nok');
                setAddScan1(''); setAddScan2(''); setAddScanValidated(false);
                setTimeout(() => addScan1Ref.current?.focus(), 100);
                return;
            }
            setAddScanError('');
            setAddScanValidated(true);
            setAddValidatedBorder(true);
            playSound('ok');
            setTimeout(() => setAddValidatedBorder(false), 1000);

            // Check product in database
            try {
                const [lookupRes, priceRes] = await Promise.all([
                    api.get(`/products/lookup/${addScan1.trim()}`).catch(() => ({ data: { found: false } })),
                    api.get(`/depot/v2/product-price/${addScan1.trim()}`).catch(() => ({ data: { price: 0, source: 'none' } }))
                ]);
                
                const priceData = priceRes.data || { price: 0, source: 'none' };
                setAddFetchedPrice(priceData.price || 0);
                setAddPriceSource(priceData.source || 'none');
                if (priceData.price > 0) {
                    setAddPurchasePrice(priceData.price.toFixed(2));
                    toast.info(`Preț preluat: ${priceData.price.toFixed(2)} RON`);
                }
                
                // Fetch min sale price from orders
                const minPriceRes = await api.post('/products/last-prices/batch', { barcodes: [addScan1.trim().toUpperCase()] });
                const minPrice = minPriceRes.data?.prices?.[addScan1.trim().toUpperCase()] || 0;
                setAddFetchedMinPrice(minPrice);
                if (minPrice > 0) {
                    setAddMinSalePrice(minPrice.toFixed(2));
                    // Auto-calculate purchase price as 80% of min sale price
                    const autoPurchasePrice = (minPrice * 0.8).toFixed(2);
                    setAddPurchasePrice(autoPurchasePrice);
                    toast.info(`Preț achiziție auto: ${autoPurchasePrice} RON (80% din vânzare min ${minPrice.toFixed(2)})`);
                }
                
                if (lookupRes.data?.found && lookupRes.data?.product) {
                    const product = lookupRes.data.product;
                    setAddExistingProduct(product);
                    setAddDescription(product.name || '');
                    toast.success(`Produs găsit: ${product.name}`);
                    setTimeout(() => { addQtyRef.current?.focus(); addQtyRef.current?.select(); }, 100);
                } else {
                    setAddExistingProduct(null);
                    setTimeout(() => addDescRef.current?.focus(), 100);
                }
            } catch (err) {
                console.error('Lookup error:', err);
                setAddExistingProduct(null);
                setTimeout(() => addDescRef.current?.focus(), 100);
            }
        }
    };

    const handleAddDescKeyDown = (e) => {
        if (e.key === 'Enter' && addDescription.trim()) {
            e.preventDefault();
            addQtyRef.current?.focus();
            addQtyRef.current?.select();
        }
    };

    const handleAddQtyKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddSubmit(); }
    };

    const handleAddPriceKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddSubmit(); }
    };

    const handleAddMinPriceKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddSubmit(); }
    };

    const handleAddSubmit = async () => {
        if (addSubmitting) return;
        if (!addScanValidated || !addDescription.trim()) {
            toast.error('Completează descrierea produsului');
            return;
        }
        if (addQty > 100) { toast.error('Cantitatea nu poate depăși 100'); return; }
        const pp = addPurchasePrice ? parseFloat(addPurchasePrice) : 0;
        if (pp > 100) { setAddConfirmHigh({ type: 'purchase', value: pp }); return; }
        const mp = addMinSalePrice ? parseFloat(addMinSalePrice) : 0;
        if (mp > 100) { setAddConfirmHigh({ type: 'minSale', value: mp }); return; }
        await doAddSubmit();
    };

    const doAddSubmit = async () => {
        setAddConfirmHigh(null);
        setAddSubmitting(true);
        const barcode = normalizeBarcode(addScan1.trim());
        const pp = addPurchasePrice ? parseFloat(addPurchasePrice) : 0;
        const mp = addMinSalePrice ? parseFloat(addMinSalePrice) : 0;
        try {
            const res = await api.post('/store/v2/add-product', {
                barcode,
                name: addDescription.trim(),
                quantity: addQty,
                purchase_price: pp,
                min_sold_price: mp
            });
            toast.success(res.data.message);
            playSound('ok');
            if (addExistingProduct && addDescription.trim() !== addExistingProduct.name) {
                await api.post('/products/v2/update-name', { barcode, name: addDescription.trim() });
            }
            // Add to recent items
            setAddRecentItems(prev => [
                { barcode, name: addDescription.trim(), quantity: addQty, price: pp, minPrice: mp, time: new Date() },
                ...prev
            ].slice(0, 5));
            resetAddForm();
            await loadStore();
            setTimeout(() => addScan1Ref.current?.focus(), 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            playSound('nok');
        }
        setAddSubmitting(false);
    };

    const getAddScanBorderClass = () => {
        if (addScanError) return 'border-red-500';
        if (addValidatedBorder) return 'border-green-500';
        if (addScanValidated) return 'border-green-500/50';
        return 'border-slate-600';
    };
    
    const handleScannedBarcode = (barcode) => {
        setShowScanner(false);
        // Normalize barcode: remove spaces, max 16 chars, uppercase
        const normalizedBarcode = normalizeBarcode(barcode);
        // Find product in store
        const found = items.find(item => item.barcode === normalizedBarcode);
        if (found) {
            setScannedProduct(found);
            setShowScanChoice(true);
        } else {
            toast.error(`Produsul cu codul ${normalizedBarcode} nu există în magazin`);
        }
    };
    
    const handleChoiceDepot = () => {
        setShowScanChoice(false);
        openMoveDepot(scannedProduct);
    };
    
    const handleChoiceScrap = () => {
        setShowScanChoice(false);
        openMoveScrap(scannedProduct);
    };
    
    // Handle scan from depot - search for product in depot and move to store
    const handleDepotScan = async (barcode) => {
        try {
            // Normalize barcode: remove spaces, max 16 chars, uppercase
            const normalizedBarcode = normalizeBarcode(barcode);
            // Search in depot for this barcode
            const res = await api.get(`/depot/v2/search/${normalizedBarcode}`);
            if (res.data && res.data.total_quantity > 0) {
                setDepotItemInfo(res.data);
                setDepotMoveQty(1);
                setShowDepotMoveModal(true);
                // Keep scanner open for continuous scanning
            } else {
                toast.error(`Produsul ${normalizedBarcode} nu există în depozit`);
            }
        } catch (err) {
            toast.error(`Produsul ${normalizeBarcode(barcode)} nu a fost găsit în depozit`);
        }
    };
    
    const handleMoveFromDepot = async () => {
        if (!depotItemInfo) return;
        if (depotMoveQty < 1 || depotMoveQty > depotItemInfo.total_quantity) {
            toast.error('Cantitate invalidă');
            return;
        }
        try {
            await api.post('/depot/v2/move-to-store', {
                barcode: depotItemInfo.barcode,
                quantity: depotMoveQty
            });
            toast.success(`${depotMoveQty} buc "${depotItemInfo.name}" mutate în magazin!`);
            setShowDepotMoveModal(false);
            setDepotItemInfo(null);
            loadStore();
            
            // Reopen scanner for continuous scanning
            setShowDepotScanner(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    const loadStore = async () => {
        setLoading(true);
        try {
            const [itemsRes, summaryRes, productsRes] = await Promise.all([
                api.get('/store/v2'),
                api.get('/store/v2/summary'),
                api.get('/products/v2')
            ]);
            
            // Build product metadata map
            const productMap = new Map();
            (productsRes.data || []).forEach(p => {
                productMap.set(p.barcode, p);
            });
            
            // Merge items with product metadata (image_url)
            const loadedItems = (itemsRes.data || []).map(item => ({
                ...item,
                image_url: productMap.get(item.barcode)?.image_url || null
            }));
            
            setItems(loadedItems);
            setSummary(summaryRes.data || { total_quantity: 0, total_value: 0, unique_products: 0 });
            
            // Initialize all categories as expanded
            const categories = [...new Set(loadedItems.map(i => i.category || 'Necategorizat'))];
            const expanded = {};
            categories.forEach(cat => expanded[cat] = true);
            setExpandedCategories(expanded);
            
            // Fetch price data for all items (pret_mediu_min, pret_vanzare_min)
            fetchPriceData(loadedItems);
        } catch (err) {
            console.error('Error loading store:', err);
        }
        setLoading(false);
    };
    
    // Fetch price data for products (batch fetch)
    const fetchPriceData = async (itemsList) => {
        const cache = {};
        // Fetch in batches of 10 to avoid overwhelming the server
        const batchSize = 10;
        for (let i = 0; i < itemsList.length; i += batchSize) {
            const batch = itemsList.slice(i, i + batchSize);
            const promises = batch.map(async (item) => {
                try {
                    const res = await api.get(`/depot/v2/product-price/${item.barcode}`);
                    return { barcode: item.barcode, data: res.data };
                } catch (err) {
                    return { barcode: item.barcode, data: { pret_mediu_min: 0, pret_vanzare_min: 0 } };
                }
            });
            const results = await Promise.all(promises);
            results.forEach(r => {
                cache[r.barcode] = r.data;
            });
        }
        setPriceDataCache(cache);
    };
    
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const loadDepotPallets = async () => {
        try {
            const res = await api.get('/depot/v2/pallets');
            setDepotPallets(res.data || []);
        } catch (err) {
            console.error('Error loading pallets:', err);
        }
    };

    const openMoveDepot = (item) => {
        setSelectedItem(item);
        setMoveQty(1);
        setSelectedPallet(null);
        setSelectedBox(null);
        loadDepotPallets();
        setShowMoveDepot(true);
    };

    const openMoveScrap = (item) => {
        setSelectedItem(item);
        setMoveQty(1);
        setScrapReason('Deteriorat');
        setShowMoveScrap(true);
    };

    const createPalletAndSelect = async () => {
        if (!newPalletNumber) {
            toast.error('Introdu numărul paletului');
            return;
        }
        try {
            const res = await api.post('/depot/v2/pallets', { number: parseInt(newPalletNumber) });
            toast.success(`Palet #${newPalletNumber} creat!`);
            setShowNewPallet(false);
            setNewPalletNumber('');
            await loadDepotPallets();
            setSelectedPallet(res.data);
            setSelectedBox(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare');
        }
    };

    const createBoxAndSelect = async () => {
        if (!newBoxName.trim() || !selectedPallet) {
            toast.error('Introdu numele cutiei');
            return;
        }
        try {
            const res = await api.post('/depot/v2/boxes', { 
                pallet_id: selectedPallet.id, 
                name: newBoxName.trim() 
            });
            toast.success(`Cutia "${newBoxName}" creată!`);
            setShowNewBox(false);
            setNewBoxName('');
            await loadDepotPallets();
            // Find updated pallet and set box
            const updatedPallets = (await api.get('/depot/v2/pallets')).data;
            const updatedPallet = updatedPallets.find(p => p.id === selectedPallet.id);
            setSelectedPallet(updatedPallet);
            const newBox = updatedPallet?.boxes?.find(b => b.name === newBoxName.trim()) || res.data;
            setSelectedBox(newBox);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare');
        }
    };

    const handleMoveToDepot = async () => {
        if (!selectedPallet || !selectedBox) {
            toast.error('Selectează paletul și cutia');
            return;
        }
        if (moveQty < 1 || moveQty > selectedItem.quantity) {
            toast.error('Cantitate invalidă');
            return;
        }
        try {
            await api.post('/depot/v2/move', {
                store_item_barcode: selectedItem.barcode,
                quantity: moveQty,
                pallet_id: selectedPallet.id,
                box_id: selectedBox.id
            });
            toast.success(`${moveQty} buc mutate în depozit!`);
            setShowMoveDepot(false);
            loadStore();
            
            // Reopen scanner for continuous scanning
            setShowScanner(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    // Save category for a product
    const handleSaveCategory = async (barcode) => {
        if (!editCategoryValue.trim()) {
            toast.error('Introdu o categorie');
            return;
        }
        try {
            await api.put(`/store/v2/items/${barcode}/category`, {
                category: editCategoryValue.trim()
            });
            toast.success('Categorie salvată!');
            setEditingCategoryBarcode(null);
            loadStore();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvare');
        }
    };

    const handleMoveToScrap = async () => {
        if (moveQty < 1 || moveQty > selectedItem.quantity) {
            toast.error('Cantitate invalidă');
            return;
        }
        try {
            await api.post('/scrap/v2/move', {
                store_item_barcode: selectedItem.barcode,
                quantity: moveQty,
                reason: scrapReason
            });
            toast.success(`${moveQty} buc mutate în SCRAP!`);
            setShowMoveScrap(false);
            loadStore();
            
            // Reopen scanner for continuous scanning
            setShowScanner(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    // Filter items by search
    const filteredItems = items.filter(item => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.barcode?.toLowerCase().includes(searchLower) ||
            item.category?.toLowerCase().includes(searchLower)
        );
    });
    
    // Sort items
    const sortedItems = useMemo(() => {
        const sorted = [...filteredItems].sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
                case 'name':
                    comparison = (a.name || '').localeCompare(b.name || '', 'ro');
                    break;
                case 'barcode':
                    comparison = (a.barcode || '').localeCompare(b.barcode || '');
                    break;
                case 'quantity':
                    comparison = (a.quantity || 0) - (b.quantity || 0);
                    break;
                case 'category':
                    comparison = (a.category || 'Necategorizat').localeCompare(b.category || 'Necategorizat', 'ro');
                    break;
                default:
                    comparison = 0;
            }
            
            return sortDir === 'asc' ? comparison : -comparison;
        });
        return sorted;
    }, [filteredItems, sortBy, sortDir]);
    
    // Get available letters from items (for alphabet navigator)
    const availableLetters = useMemo(() => {
        const letters = new Set();
        sortedItems.forEach(item => {
            const firstLetter = (item.name || '').charAt(0).toUpperCase();
            if (firstLetter && /[A-ZĂÎÂȘȚ0-9]/.test(firstLetter)) {
                letters.add(firstLetter);
            }
        });
        return Array.from(letters).sort((a, b) => a.localeCompare(b, 'ro'));
    }, [sortedItems]);
    
    // Scroll to letter
    const scrollToLetter = useCallback((letter) => {
        const item = sortedItems.find(i => 
            (i.name || '').charAt(0).toUpperCase() === letter
        );
        if (item && itemRefs.current[item.barcode]) {
            itemRefs.current[item.barcode].scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
            });
        }
    }, [sortedItems]);

    // Group by category (using sorted items)
    const groupedByCategory = sortedItems.reduce((acc, item) => {
        const cat = item.category || 'Necategorizat';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {});
    
    // Handle sort button click
    const handleSort = (field) => {
        if (sortBy === field) {
            // Toggle direction
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to asc
            setSortBy(field);
            setSortDir('asc');
        }
    };
    
    // Get sort icon
    const SortIcon = ({ field }) => {
        if (sortBy !== field) return <ArrowUpDown size={12} className="text-zinc-300" />;
        return sortDir === 'asc' 
            ? <ArrowUp size={12} className="text-violet-600" /> 
            : <ArrowDown size={12} className="text-violet-600" />;
    };

    if (loading) {
        return (
            <div className="pb-24">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Magazin
                    </h1>
                    <p className="text-zinc-500 mt-1">Produse în stoc</p>
                </div>
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Magazin
                    </h1>
                    <p className="text-zinc-500 mt-1">Produse în stoc</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => { resetAddForm(); setShowAddModal(true); setTimeout(() => addScan1Ref.current?.focus(), 200); }}
                        className="bg-violet-500 hover:bg-violet-600"
                        data-testid="add-product-btn"
                    >
                        <Plus size={18} className="mr-2" />
                        Adaugă
                    </Button>
                    <Button
                        onClick={() => setShowDepotScanner(true)}
                        className="bg-orange-500 hover:bg-orange-600"
                        data-testid="scan-from-depot-btn"
                    >
                        <Warehouse size={18} className="mr-2" />
                        Din Depozit
                    </Button>
                    <Button
                        onClick={async () => {
                            try {
                                toast.info('Se pregătește exportul...');
                                const response = await fetch(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/store/v2/export/excel`,
                                    { headers: { 'X-App-Version': localStorage.getItem('app_version') || 'v2' } }
                                );
                                if (!response.ok) throw new Error('Export failed');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'stoc_magazin.xlsx';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                                toast.success('Export descărcat!');
                            } catch (err) {
                                console.error(err);
                                toast.error('Eroare la export');
                            }
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600"
                    >
                        <FileSpreadsheet size={18} className="mr-2" />
                        Export Excel
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white p-4 rounded-xl border border-zinc-200 text-center">
                    <p className="text-xs text-zinc-500 mb-1">Produse Unice</p>
                    <p className="text-2xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {items.length}
                    </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-zinc-200 text-center">
                    <p className="text-xs text-zinc-500 mb-1">Total Bucăți</p>
                    <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                    </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-zinc-200 text-center">
                    <p className="text-xs text-zinc-500 mb-1">Val Tot</p>
                    <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatPrice(items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.display_price || 0)), 0))} <span className="text-sm font-normal">RON</span>
                    </p>
                    {isV3() && items.length > 0 && (
                        <div className="mt-1 text-xs text-zinc-500">
                            <span>Fără TVA: {formatPrice(getPriceWithoutTVA(items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.display_price || 0)), 0)))}</span>
                            <span className="mx-1">|</span>
                            <span className="text-blue-600">TVA: {formatPrice(getTVAAmount(items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.display_price || 0)), 0)))}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Search */}
            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Caută produs, cod sau categorie..."
                    className="pl-10"
                />
            </div>
            
            {/* Sort Bar */}
            <div className="flex items-center gap-2 mb-4 p-2 bg-zinc-100 rounded-lg">
                <span className="text-xs text-zinc-500 mr-2">Sortare:</span>
                <button
                    onClick={() => handleSort('name')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                        sortBy === 'name' ? 'bg-violet-100 text-violet-700' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                    }`}
                >
                    Nume <SortIcon field="name" />
                </button>
                <button
                    onClick={() => handleSort('barcode')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                        sortBy === 'barcode' ? 'bg-violet-100 text-violet-700' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                    }`}
                >
                    Cod <SortIcon field="barcode" />
                </button>
                <button
                    onClick={() => handleSort('quantity')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                        sortBy === 'quantity' ? 'bg-violet-100 text-violet-700' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                    }`}
                >
                    QTY <SortIcon field="quantity" />
                </button>
                <button
                    onClick={() => handleSort('category')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                        sortBy === 'category' ? 'bg-violet-100 text-violet-700' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                    }`}
                >
                    Categorie <SortIcon field="category" />
                </button>
            </div>
            
            {/* Alphabet Navigator */}
            {sortedItems.length > 0 && sortBy === 'name' && (
                <div className="flex flex-wrap gap-1 mb-4 p-2 bg-white border border-zinc-200 rounded-lg">
                    <span className="text-xs text-zinc-500 mr-2 self-center">Salt la:</span>
                    {availableLetters.map(letter => (
                        <button
                            key={letter}
                            onClick={() => scrollToLetter(letter)}
                            className="w-8 h-8 flex items-center justify-center rounded bg-violet-50 hover:bg-violet-100 text-violet-700 font-bold text-sm transition-colors"
                        >
                            {letter}
                        </button>
                    ))}
                </div>
            )}

            {/* Items List - Simple flat list sorted alphabetically */}
            {sortedItems.length === 0 ? (
                <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-8 text-center">
                    <Store size={48} className="mx-auto text-zinc-400 mb-3" />
                    <h2 className="font-bold text-zinc-700 mb-2">Magazin gol</h2>
                    <p className="text-zinc-500">
                        {search ? 'Niciun produs găsit' : 'Adaugă produse prin Recepție Palet'}
                    </p>
                </div>
            ) : (
                <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                    {/* Header row */}
                    <div className="flex items-center gap-2 px-2 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-medium text-zinc-500">
                        <div className="w-12 shrink-0">Img</div>
                        <div className="w-14 shrink-0">Acțiuni</div>
                        <div className="flex-1">Nume</div>
                        <div className="w-28 shrink-0">Cod</div>
                        <div className="w-24 shrink-0">Categorie</div>
                        <div className="w-12 text-right shrink-0">QTY</div>
                        <div className="w-16 text-right shrink-0 text-blue-600" title="Preț Mediu (Min Achiziție)">Ach. Min</div>
                        <div className="w-16 text-right shrink-0 text-green-600" title="Preț Estimat (Min Vânzare)">Vânz. Min</div>
                        <div className="w-20 text-right shrink-0 text-zinc-400" title="Data Intrare Stoc">Intrare</div>
                        {isV3() && (
                            <>
                                <div className="w-16 text-right shrink-0">Fără TVA</div>
                                <div className="w-14 text-right shrink-0">TVA</div>
                            </>
                        )}
                        <div className="w-14 text-right shrink-0">{isV3() ? 'Total' : 'Val Est.'}</div>
                    </div>
                    
                    {/* Items */}
                    {sortedItems.map((item, idx) => (
                        <div
                            key={item.id}
                            ref={el => itemRefs.current[item.barcode] = el}
                            className={`flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 transition-colors ${idx !== sortedItems.length - 1 ? 'border-b border-zinc-100' : ''}`}
                        >
                            {/* Image */}
                            <div className="w-12 shrink-0">
                                {item.image_url ? (
                                    <div 
                                        className="w-10 h-10 rounded-lg border overflow-hidden bg-white cursor-pointer hover:ring-2 hover:ring-violet-400 transition-all"
                                        onClick={() => setLightboxImage({ url: item.image_url, name: item.name })}
                                    >
                                        <img 
                                            src={item.image_url} 
                                            alt={item.name}
                                            className="w-full h-full object-contain"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 rounded-lg border-2 border-dashed border-zinc-300 flex items-center justify-center bg-zinc-50">
                                        <Image size={14} className="text-zinc-400" />
                                    </div>
                                )}
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex gap-0.5 shrink-0 w-14">
                                <button
                                    onClick={() => openMoveDepot(item)}
                                    className="p-1 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                                    title="Depozit"
                                >
                                    <Warehouse size={12} />
                                </button>
                                <button
                                    onClick={() => openMoveScrap(item)}
                                    className="p-1 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                    title="SCRAP"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                            
                            {/* Product name */}
                            <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-zinc-900 truncate block">{item.name}</span>
                            </div>
                            
                            {/* Barcode */}
                            <div className="w-28 shrink-0">
                                <span className="text-xs text-zinc-400 font-mono">{item.barcode}</span>
                            </div>
                            
                            {/* Category - editable if empty or "Necategorizat" */}
                            <div className="w-28 shrink-0">
                                {editingCategoryBarcode === item.barcode ? (
                                    <div className="flex gap-1">
                                        <Input
                                            value={editCategoryValue}
                                            onChange={(e) => setEditCategoryValue(e.target.value)}
                                            className="h-6 text-xs px-1"
                                            placeholder="Categorie"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSaveCategory(item.barcode);
                                                } else if (e.key === 'Escape') {
                                                    setEditingCategoryBarcode(null);
                                                }
                                            }}
                                        />
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0"
                                            onClick={() => handleSaveCategory(item.barcode)}
                                        >
                                            <Check size={12} className="text-green-600" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0"
                                            onClick={() => setEditingCategoryBarcode(null)}
                                        >
                                            <X size={12} className="text-red-600" />
                                        </Button>
                                    </div>
                                ) : (
                                    <span 
                                        className={`text-xs px-1.5 py-0.5 rounded cursor-pointer hover:ring-2 hover:ring-violet-300 ${
                                            !item.category || item.category === 'Necategorizat' 
                                                ? 'bg-orange-50 text-orange-600' 
                                                : 'bg-violet-50 text-violet-600'
                                        }`}
                                        onClick={() => {
                                            setEditingCategoryBarcode(item.barcode);
                                            setEditCategoryValue(item.category === 'Necategorizat' ? '' : (item.category || ''));
                                        }}
                                        title="Click pentru a edita categoria"
                                    >
                                        {item.category || 'Necategorizat'}
                                    </span>
                                )}
                            </div>
                            
                            {/* Quantity */}
                            <div className="w-12 text-right shrink-0">
                                <span className="text-sm font-bold text-zinc-900">{item.quantity}</span>
                            </div>
                            
                            {/* Preț Mediu (Min Achiziție) */}
                            <div className="w-16 text-right shrink-0">
                                {priceDataCache[item.barcode]?.pret_mediu_min > 0 ? (
                                    <span className="text-xs font-medium text-blue-600">
                                        {formatPrice(priceDataCache[item.barcode].pret_mediu_min)}
                                    </span>
                                ) : (
                                    <span className="text-xs text-zinc-300">-</span>
                                )}
                            </div>
                            
                            {/* Preț Estimat (Min Vânzare) */}
                            <div className="w-16 text-right shrink-0">
                                {priceDataCache[item.barcode]?.pret_vanzare_min > 0 ? (
                                    <span className="text-xs font-medium text-green-600">
                                        {formatPrice(priceDataCache[item.barcode].pret_vanzare_min)}
                                    </span>
                                ) : (
                                    <span className="text-xs text-zinc-300">-</span>
                                )}
                            </div>
                            
                            {/* Data Intrare Stoc */}
                            <div className="w-20 text-right shrink-0">
                                {item.entry_timestamp || item.created_at ? (
                                    <span className="text-xs text-zinc-400">
                                        {new Date(item.entry_timestamp || item.created_at).toLocaleDateString('ro-RO')}
                                    </span>
                                ) : (
                                    <span className="text-xs text-zinc-300 italic">Stoc Inițial</span>
                                )}
                            </div>
                            
                            {/* TVA columns - only in V3 */}
                            {isV3() && (
                                <>
                                    <div className="w-16 text-right shrink-0">
                                        <span className="text-xs text-zinc-600">
                                            {formatPrice(getPriceWithoutTVA(item.display_price))}
                                        </span>
                                    </div>
                                    <div className="w-14 text-right shrink-0">
                                        <span className="text-xs text-blue-600">
                                            {formatPrice(getTVAAmount(item.display_price))}
                                        </span>
                                    </div>
                                </>
                            )}
                            
                            {/* Price (Total) */}
                            <div className="w-14 text-right shrink-0">
                                <span className="text-sm font-bold text-amber-600">
                                    {formatPrice(item.display_price)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Move to Depot Modal */}
            <Dialog open={showMoveDepot} onOpenChange={setShowMoveDepot}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warehouse size={20} className="text-indigo-600" />
                            Mută în Depozit
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <div className="space-y-4 mt-4">
                            {/* Product info */}
                            <div className="p-3 bg-zinc-100 rounded-lg">
                                <p className="font-medium text-zinc-900">{selectedItem.name}</p>
                                <p className="text-sm text-zinc-500">Disponibil: {selectedItem.quantity} buc</p>
                            </div>
                            
                            {/* Quantity */}
                            <div>
                                <Label>Cantitate de mutat</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={selectedItem.quantity}
                                    value={moveQty}
                                    onChange={(e) => setMoveQty(parseInt(e.target.value) || 1)}
                                    className="mt-1"
                                />
                            </div>
                            
                            {/* Pallet Selection */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Palet</Label>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-indigo-600 h-6 px-2"
                                        onClick={() => setShowNewPallet(true)}
                                    >
                                        <Plus size={14} className="mr-1" />
                                        Nou
                                    </Button>
                                </div>
                                {showNewPallet ? (
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            placeholder="Nr. palet"
                                            value={newPalletNumber}
                                            onChange={(e) => setNewPalletNumber(e.target.value)}
                                            className="flex-1"
                                            autoFocus
                                        />
                                        <Button size="sm" onClick={createPalletAndSelect} className="bg-indigo-500">
                                            Creează
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setShowNewPallet(false)}>
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedPallet?.id || ''}
                                        onValueChange={(val) => {
                                            const pallet = depotPallets.find(p => p.id === val);
                                            setSelectedPallet(pallet);
                                            setSelectedBox(null);
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Selectează palet..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {depotPallets.map(p => (
                                                <SelectItem key={p.id} value={p.id}>
                                                    Palet #{p.number} ({p.total_boxes} cutii)
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                            
                            {/* Box Selection */}
                            {selectedPallet && (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label>Cutie</Label>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-violet-600 h-6 px-2"
                                            onClick={() => setShowNewBox(true)}
                                        >
                                            <Plus size={14} className="mr-1" />
                                            Nouă
                                        </Button>
                                    </div>
                                    {showNewBox ? (
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder="Nume cutie"
                                                value={newBoxName}
                                                onChange={(e) => setNewBoxName(e.target.value)}
                                                className="flex-1"
                                                autoFocus
                                            />
                                            <Button size="sm" onClick={createBoxAndSelect} className="bg-violet-500">
                                                Creează
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setShowNewBox(false)}>
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Select
                                            value={selectedBox?.id || ''}
                                            onValueChange={(val) => {
                                                const box = selectedPallet?.boxes?.find(b => b.id === val);
                                                setSelectedBox(box);
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Selectează cutie..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {selectedPallet?.boxes?.map(b => (
                                                    <SelectItem key={b.id} value={b.id}>
                                                        {b.name} ({b.total_items} buc)
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                            )}
                            
                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <Button variant="outline" onClick={() => setShowMoveDepot(false)} className="flex-1">
                                    Renunță
                                </Button>
                                <Button 
                                    onClick={handleMoveToDepot} 
                                    className="flex-1 bg-indigo-500 hover:bg-indigo-600"
                                    disabled={!selectedPallet || !selectedBox}
                                >
                                    <ArrowRight size={16} className="mr-2" />
                                    Mută
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Move to Scrap Modal */}
            <Dialog open={showMoveScrap} onOpenChange={setShowMoveScrap}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 size={20} />
                            Mută în SCRAP
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <div className="space-y-4 mt-4">
                            {/* Product info */}
                            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                                <p className="font-medium text-zinc-900">{selectedItem.name}</p>
                                <p className="text-sm text-zinc-500">Disponibil: {selectedItem.quantity} buc</p>
                            </div>
                            
                            {/* Quantity */}
                            <div>
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={selectedItem.quantity}
                                    value={moveQty}
                                    onChange={(e) => setMoveQty(parseInt(e.target.value) || 1)}
                                    className="mt-1"
                                />
                            </div>
                            
                            {/* Reason */}
                            <div>
                                <Label>Motiv</Label>
                                <Select value={scrapReason} onValueChange={setScrapReason}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Selectează motiv..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {scrapReasons.map(reason => (
                                            <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <Button variant="outline" onClick={() => setShowMoveScrap(false)} className="flex-1">
                                    Renunță
                                </Button>
                                <Button 
                                    onClick={handleMoveToScrap} 
                                    className="flex-1 bg-red-500 hover:bg-red-600"
                                >
                                    <Trash2 size={16} className="mr-2" />
                                    SCRAP
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Scan Choice Modal */}
            <Dialog open={showScanChoice} onOpenChange={setShowScanChoice}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Unde muți produsul?</DialogTitle>
                    </DialogHeader>
                    
                    {scannedProduct && (
                        <div className="space-y-4 mt-4">
                            {/* Product info */}
                            <div className="p-3 bg-zinc-100 rounded-lg">
                                <p className="font-medium text-zinc-900">{scannedProduct.name}</p>
                                <p className="text-sm text-zinc-500">Disponibil: {scannedProduct.quantity} buc</p>
                                <p className="text-xs text-zinc-400 font-mono">{scannedProduct.barcode}</p>
                            </div>
                            
                            {/* Choice buttons */}
                            <div className="flex flex-col gap-3">
                                <Button 
                                    onClick={handleChoiceDepot}
                                    className="w-full bg-indigo-500 hover:bg-indigo-600 h-14"
                                >
                                    <Warehouse size={20} className="mr-2" />
                                    Mută în Depozit
                                </Button>
                                <Button 
                                    onClick={handleChoiceScrap}
                                    className="w-full bg-red-500 hover:bg-red-600 h-14"
                                >
                                    <Trash2 size={20} className="mr-2" />
                                    Mută în SCRAP
                                </Button>
                            </div>
                            
                            <Button variant="outline" onClick={() => setShowScanChoice(false)} className="w-full">
                                Renunță
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleScannedBarcode}
            />
            
            {/* Depot Scanner - Pentru mutare din depozit în magazin */}
            <BarcodeScanner
                isOpen={showDepotScanner}
                onClose={() => setShowDepotScanner(false)}
                onScan={handleDepotScan}
                keepOpen={true}
                title="Scanare din Depozit - Mută în Magazin"
            />
            
            {/* Move from Depot Modal */}
            <Dialog open={showDepotMoveModal} onOpenChange={(open) => {
                setShowDepotMoveModal(open);
                if (!open) setDepotItemInfo(null);
            }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <ArrowRight className="inline mr-2" size={20} />
                            Mută în Magazin
                        </DialogTitle>
                    </DialogHeader>
                    
                    {depotItemInfo && (
                        <div className="space-y-4 mt-4">
                            {/* Product info */}
                            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <p className="font-medium text-zinc-900">{depotItemInfo.name}</p>
                                <p className="text-sm text-orange-600">
                                    În depozit: {depotItemInfo.total_quantity} buc
                                </p>
                                <p className="text-xs text-zinc-400 font-mono">{depotItemInfo.barcode}</p>
                            </div>
                            
                            {/* Quantity selector */}
                            <div>
                                <Label>Cantitate de mutat</Label>
                                <div className="flex items-center gap-3 mt-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDepotMoveQty(Math.max(1, depotMoveQty - 1))}
                                        className="w-12 h-12"
                                    >
                                        -
                                    </Button>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={depotItemInfo.total_quantity}
                                        value={depotMoveQty}
                                        onChange={(e) => setDepotMoveQty(Math.min(depotItemInfo.total_quantity, Math.max(1, parseInt(e.target.value) || 1)))}
                                        className="text-center text-2xl font-bold w-24 h-12"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDepotMoveQty(Math.min(depotItemInfo.total_quantity, depotMoveQty + 1))}
                                        className="w-12 h-12"
                                    >
                                        +
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDepotMoveQty(depotItemInfo.total_quantity)}
                                        className="h-12"
                                    >
                                        Tot
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-3 pt-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        setShowDepotMoveModal(false);
                                        setDepotItemInfo(null);
                                    }}
                                    className="flex-1"
                                >
                                    Renunță
                                </Button>
                                <Button
                                    onClick={handleMoveFromDepot}
                                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                                >
                                    <Store size={18} className="mr-2" />
                                    Mută în Magazin
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* Image Lightbox - larger */}
            {lightboxImage && (
                <div 
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setLightboxImage(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl">
                        <button
                            onClick={() => setLightboxImage(null)}
                            className="absolute top-3 right-3 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white z-10"
                        >
                            <X size={20} />
                        </button>
                        <img 
                            src={lightboxImage.url} 
                            alt={lightboxImage.name}
                            className="max-w-full max-h-[85vh] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className="p-4 bg-zinc-100 text-center">
                            <p className="font-medium text-zinc-900 text-lg">{lightboxImage.name}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* ADD PRODUCT Modal — Structură identică cu Depozit */}
            <Dialog open={showAddModal} onOpenChange={(v) => { if (!v) resetAddForm(); setShowAddModal(v); }}>
                <DialogContent className="bg-slate-950 border-amber-500/50 max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
                    <DialogHeader className="px-4 pt-4 pb-2 border-b border-slate-800">
                        <DialogTitle className="text-amber-400 flex items-center gap-2">
                            <Scan size={20} />
                            Adaugă produs în Magazin
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex flex-col flex-1 overflow-hidden px-4 py-3">
                        {/* Scan Section - FIXED */}
                        <div className="flex-shrink-0 bg-slate-800/50 rounded-lg p-3 space-y-3">
                            {/* Scan Error */}
                            {addScanError && (
                                <div className="flex items-center gap-2 p-2 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm">
                                    <XCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{addScanError}</span>
                                </div>
                            )}
                            
                            {addShortCodeWarning && !addScanError && (
                                <div className="flex items-center gap-2 p-2 bg-yellow-900/30 border border-yellow-500/50 rounded-lg text-yellow-400 text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>Cod scurt - verifică eticheta!</span>
                                </div>
                            )}

                            {addScanValidated && !addScanError && (
                                <div className="flex items-center gap-2 p-2 bg-green-900/30 border border-green-500/50 rounded-lg text-green-400 text-sm">
                                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                                    <span>Cod validat: {addScan1}</span>
                                </div>
                            )}

                            {/* Scan Fields */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-400 text-xs">Scanare 1 *</Label>
                                    <Input
                                        ref={addScan1Ref}
                                        type="text"
                                        placeholder="Scanează codul..."
                                        value={addScan1}
                                        onChange={handleAddScan1Change}
                                        onKeyDown={handleAddScan1KeyDown}
                                        disabled={addScanValidated}
                                        className={`bg-slate-900 text-white transition-all ${getAddScanBorderClass()} ${addScanValidated ? 'opacity-60' : ''}`}
                                        data-testid="add-scan1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Scanare 2 (verificare) *</Label>
                                    <Input
                                        ref={addScan2Ref}
                                        type="text"
                                        placeholder="Scanează din nou..."
                                        value={addScan2}
                                        onChange={handleAddScan2Change}
                                        onKeyDown={handleAddScan2KeyDown}
                                        disabled={addScanValidated || !addScan1.trim()}
                                        className={`bg-slate-900 text-white transition-all ${getAddScanBorderClass()} ${addScanValidated ? 'opacity-60' : ''} ${!addScan1.trim() ? 'opacity-40' : ''}`}
                                        data-testid="add-scan2"
                                    />
                                </div>
                            </div>

                            {/* Form fields after validation */}
                            {addScanValidated && (
                                <>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-slate-400 text-xs">
                                                Descriere {addExistingProduct ? '(auto-completat)' : '*'}
                                            </Label>
                                            <Input
                                                ref={addDescRef}
                                                type="text"
                                                placeholder="Descriere produs..."
                                                value={addDescription}
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (val.length === 1) val = val.charAt(0).toUpperCase();
                                                    else if (val.length > 1 && val.charAt(0) !== val.charAt(0).toUpperCase()) val = val.charAt(0).toUpperCase() + val.slice(1);
                                                    setAddDescription(val);
                                                }}
                                                onKeyDown={handleAddDescKeyDown}
                                                className="bg-slate-900 border-slate-600 text-white"
                                                data-testid="add-description"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-slate-400 text-xs">Cantitate</Label>
                                            <Input
                                                ref={addQtyRef}
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={addQty}
                                                onChange={(e) => setAddQty(Math.min(parseInt(e.target.value) || 1, 100))}
                                                onKeyDown={handleAddQtyKeyDown}
                                                className={`bg-slate-900 border-slate-600 text-white ${addQty >= 100 ? 'border-amber-500' : ''}`}
                                                data-testid="add-qty"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-slate-400 text-xs">
                                                Preț Achiziție (RON)
                                                {addPriceSource && addPriceSource !== 'none' && (
                                                    <span className="ml-1 text-slate-500">
                                                        ({addPriceSource === 'manual_history' ? 'Manual' : addPriceSource === 'receptie_paleti' ? 'Recepție' : 'Istoric'})
                                                    </span>
                                                )}
                                            </Label>
                                            <Input
                                                ref={addPriceRef}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="0.00"
                                                value={addPurchasePrice}
                                                onChange={(e) => setAddPurchasePrice(e.target.value)}
                                                onKeyDown={handleAddPriceKeyDown}
                                                className={`bg-slate-900 text-white ${addPurchasePrice && parseFloat(addPurchasePrice) !== addFetchedPrice ? 'border-amber-500 text-amber-300' : 'border-slate-600'}`}
                                                data-testid="add-purchase-price"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-slate-400 text-xs">
                                                Preț Vânzare Min
                                                {addFetchedMinPrice > 0 && (
                                                    <span className="ml-1 text-slate-500">(propus: {addFetchedMinPrice.toFixed(2)})</span>
                                                )}
                                            </Label>
                                            <Input
                                                ref={addMinPriceRef}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="0.00"
                                                value={addMinSalePrice}
                                                onChange={(e) => setAddMinSalePrice(e.target.value)}
                                                onKeyDown={handleAddMinPriceKeyDown}
                                                className={`bg-slate-900 text-white ${addMinSalePrice && parseFloat(addMinSalePrice) !== addFetchedMinPrice && addFetchedMinPrice > 0 ? 'border-amber-500 text-amber-300' : 'border-slate-600'}`}
                                                data-testid="add-min-sale-price"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end">
                                        <Button onClick={handleAddSubmit} disabled={addSubmitting} className="bg-violet-600 hover:bg-violet-700">
                                            {addSubmitting ? <Loader2 className="animate-spin mr-1" size={14} /> : <Plus size={14} className="mr-1" />}
                                            Adaugă în Magazin
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Istoric últime adăugate — SCROLLABLE */}
                        {addRecentItems.length > 0 && (
                            <div className="flex-1 overflow-y-auto mt-3">
                                <p className="text-xs text-slate-500 mb-2">Ultimele adăugate în această sesiune:</p>
                                <div className="space-y-1.5">
                                    {addRecentItems.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-3 py-2 text-sm">
                                            <span className="text-amber-500 font-mono w-4 text-right">{idx + 1}.</span>
                                            <span className="text-slate-300 truncate flex-1">{item.name}</span>
                                            <span className="text-slate-400 font-mono">x{item.quantity}</span>
                                            {item.price > 0 && <span className="text-green-400 font-mono">{item.price.toFixed(2)}</span>}
                                            {item.minPrice > 0 && <span className="text-amber-400 font-mono text-xs">min:{item.minPrice.toFixed(2)}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-between items-center px-4 py-3 border-t border-slate-800">
                        <p className="text-xs text-slate-500">
                            {addRecentItems.length > 0 ? `${addRecentItems.length} adăugate` : 'Scanează un cod pentru a începe'}
                        </p>
                        <Button variant="outline" onClick={() => { resetAddForm(); setShowAddModal(false); }} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                            Închide
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirm high value for add product */}
            <Dialog open={!!addConfirmHigh} onOpenChange={() => setAddConfirmHigh(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-amber-600">Confirmare valoare mare</DialogTitle>
                    </DialogHeader>
                    <p className="text-zinc-600">
                        Prețul introdus este <strong className="text-amber-600">{addConfirmHigh?.value?.toFixed(2)} RON</strong>. Ești sigur?
                    </p>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setAddConfirmHigh(null)}>Anulează</Button>
                        <Button onClick={doAddSubmit} className="bg-amber-600 hover:bg-amber-700">Da, salvează</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MagazinV2;
